import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { mapFieldErrors } from '@/api/fieldErrors';
import { ActionErrorAlert } from '@/components/ActionErrorAlert';
import { useActionError, type ActionError } from '@/components/useActionError';
import { SubmitButton } from '@/components/SubmitButton';
import { useFieldErrors } from '@/components/useFieldErrors';
import type { AdminDeletionCancelRequest, AdminDeletionRow } from '@/api/generated';

export interface CancelDeletionDialogProps {
  open: boolean;
  row: AdminDeletionRow | null;
  onClose: () => void;
  onSubmit: (id: number, body: AdminDeletionCancelRequest) => Promise<void>;
}

const REASON_MAX = 200;

const FIELDS = ['reason'] as const;

export function CancelDeletionDialog({
  open,
  row,
  onClose,
  onSubmit,
}: CancelDeletionDialogProps) {
  const { t } = useTranslation();
  const describe = useActionError();
  const reasonRef = useRef<HTMLInputElement>(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<ActionError | null>(null);
  const fields = useFieldErrors(FIELDS, { reason: reasonRef }, submitting);
  const { reset } = fields;

  useEffect(() => {
    if (!open) return;
    setReason('');
    setError(null);
    reset();
    setSubmitting(false);
  }, [open, reset]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (row?.id === undefined) return;
    setSubmitting(true);
    setError(null);
    fields.reset();
    try {
      const trimmed = reason.trim().slice(0, REASON_MAX);
      await onSubmit(row.id, trimmed.length > 0 ? { reason: trimmed } : {});
      onClose();
    } catch (err) {
      const action = describe(err);
      setError(action);
      fields.failMarked(mapFieldErrors(action.fieldKeys, { reason: 'reason' }), {
        reason: t('settings.field.reason'),
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm">
      <Box component="form" noValidate onSubmit={(e) => void handleSubmit(e)}>
        <DialogTitle>
          {t('settings.data.cancelTitle', { name: row?.displayName ?? t('settings.unavailable') })}
        </DialogTitle>
        <DialogContent>
          <ActionErrorAlert error={error} sx={{ mb: 2 }} />
          <Typography sx={{ mb: 2 }}>
            {t('settings.data.cancelBody', {
              name: row?.displayName ?? t('settings.unavailable'),
            })}
          </Typography>
          <TextField
            margin="dense"
            fullWidth
            multiline
            minRows={2}
            label={t('settings.data.reasonOptional')}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value.slice(0, REASON_MAX));
              fields.clear('reason');
            }}
            disabled={submitting}
            inputRef={reasonRef}
            error={fields.errors.reason !== undefined}
            helperText={
              fields.errors.reason ??
              t('settings.data.reasonCount', { count: reason.length, max: REASON_MAX })
            }
            slotProps={{ htmlInput: { maxLength: REASON_MAX } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>
            {t('settings.data.keep')}
          </Button>
          <SubmitButton type="submit" loading={submitting}>
            {t('settings.data.cancelAction')}
          </SubmitButton>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
